import { useParams, useNavigate, Link } from 'react-router-dom';
import { Camera, ExternalLink, RefreshCw } from 'lucide-react';
import { Button } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import {
  useServiceSnapshots,
  useInstanceSnapshots,
  type ServiceSnapshot,
} from '@/api/opennebula';
import { ListPageShell, TableSkeleton, ErrorState, linkStyle, Dash, mbToGB } from './components/ListPageShell';

/**
 * Service-wide snapshot overview. Snapshots are created/reverted/deleted from the
 * owning instance's Snapshots tab; this page aggregates them across all instances
 * (mirrors @console's top-level Snapshots list) with a per-row Increments count and
 * a "Manage" link back to the instance tab.
 */
export function Snapshots() {
  const { serviceId } = useParams<{ serviceId: string }>();
  const navigate = useNavigate();
  const { data: snapshots = [], isLoading, isError, refetch } = useServiceSnapshots(serviceId);
  const base = `/services/compute/${serviceId}`;

  const manageHref = (s: ServiceSnapshot) =>
    s.attached?.instance_id ? `${base}/instances/${s.attached.instance_id}/snapshots` : base;

  const columns: ColumnDef<ServiceSnapshot, unknown>[] = [
    {
      id: 'name',
      header: 'Name',
      cell: ({ row }) => <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>{row.original.name || row.original.id}</span>,
    },
    {
      id: 'id',
      header: 'Snapshot ID',
      cell: ({ row }) => (
        <code style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>{row.original.id}</code>
      ),
    },
    {
      id: 'instance',
      header: 'Instance',
      cell: ({ row }) =>
        row.original.attached?.instance_id ? (
          <Link to={`${base}/instances/${row.original.attached.instance_id}`} style={linkStyle}>
            {row.original.attached.name}
          </Link>
        ) : (
          <Dash />
        ),
    },
    {
      id: 'increments',
      header: 'Increments',
      cell: ({ row }) => <IncrementsCell serviceId={serviceId} instanceId={row.original.attached?.instance_id} />,
    },
    {
      id: 'size',
      header: 'Size',
      cell: ({ row }) => <span>{mbToGB(row.original.size)} GB</span>,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div style={{ textAlign: 'right' }}>
          <Button size="sm" variant="ghost" endIcon={<ExternalLink size={14} />} onClick={() => navigate(manageHref(row.original))}>
            Manage
          </Button>
        </div>
      ),
    },
  ];

  return (
    <ListPageShell
      icon={<Camera size={22} />}
      title="Snapshots"
      description="All instance snapshots across this service. Create or revert from each instance's Snapshots tab."
      actions={
        <Button size="sm" variant="secondary" startIcon={<RefreshCw size={15} />} onClick={() => refetch()}>
          Refresh
        </Button>
      }
    >
      {isLoading ? (
        <TableSkeleton />
      ) : isError ? (
        <ErrorState description="Could not load snapshots for this service." onRetry={() => refetch()} />
      ) : (
        // DataTable owns the bordered frame; the empty placeholder renders
        // inside it via `emptyState`, so empty and populated share one frame
        // (no extra Card wrapper, no double border).
        <DataTable
          columns={columns}
          data={snapshots}
          getRowId={(s) => String(s.id)}
          emptyState={
            <EmptyState
              icon={<Camera size={28} />}
              title="No snapshots"
              description="Snapshots you take from an instance's Snapshots tab will appear here."
            />
          }
        />
      )}
    </ListPageShell>
  );
}

/** Count of disk snapshots on the owning instance (mirrors @console SnapshotIncrements). */
function IncrementsCell({ serviceId, instanceId }: { serviceId?: string; instanceId?: string }) {
  const { data } = useInstanceSnapshots(serviceId, instanceId);
  if (!instanceId) return <Dash />;
  return <span style={{ color: 'var(--vn-fg-muted)' }}>{data?.length ?? 0}</span>;
}
